import { useTheme } from '@emotion/react';
import cx from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState, useEffect } from 'react';
import { BnBack, BnForward } from '../../../icons';
import { StyledCalendar } from '../style';
import { EDurations } from '../../../lib/timeUtils';

export interface DateProps {
	value?: string | Dayjs;
	defaultValue?: Dayjs;
	disabledDates?: (day: Dayjs) => boolean;
	mode?: string;
	onChange?: (value: Dayjs | undefined) => void;
}

export interface WeekDay {
	date: string;
	dayOfMonth: number;
	isCurrentMonth: boolean;
	isDisabled?: boolean;
}

const NUM_DAYS_IN_WEEK = 7;
const NUM_DAYS_IN_SIX_WEEKS = 42;
const WEEK_RANGE_SHIFT = 6;

export const Date = ({ defaultValue, disabledDates, mode = 'date', value, onChange }: DateProps) => {
	const getValueFormat = () => {
		if (mode === 'date') {
			return 'DD / MM / YYYY';
		} else if (mode === 'datetime') {
			return 'DD / MM / YYYY HH:mm';
		} else {
			return 'HH:mm';
		}
	};
	const colors = useTheme();
	const currentDate = dayjs();
	const [selectedMonth, setSelectedMonth] = useState<{
		month: number;
		year: number;
	}>({
		month: defaultValue ? dayjs(defaultValue).month() : dayjs().month(),
		year: defaultValue ? dayjs(defaultValue).year() : dayjs().year(),
	});
	const [weeksAndDays, setWeeksAndDays] = useState<any[]>([]);
	const [hoverPlaceholder, setHoverPlaceholder] = useState<string | undefined>(undefined);
	const [selectedDay, setSelectedDay] = useState<dayjs.Dayjs>(defaultValue ? dayjs(defaultValue) : dayjs());
	const [inputValue, setInputValue] = useState<string | undefined>(
		dayjs(value && dayjs(value).isValid() ? value : currentDate).format(getValueFormat())
	);
	const [isOptionsOpen, setIsOptionsOpen] = useState<boolean>(false);

	const weekDays = [
		dayjs().day(1).format('dd').slice(0, 1),
		dayjs().day(2).format('dd').slice(0, 1),
		dayjs().day(3).format('dd').slice(0, 1),
		dayjs().day(4).format('dd').slice(0, 1),
		dayjs().day(5).format('dd').slice(0, 1),
		dayjs().day(6).format('dd').slice(0, 1),
		dayjs().day(0).format('dd').slice(0, 1),
	];

	const createCalendar = () => {
		const firstDayOfMonth = dayjs(`${selectedMonth.year}-${selectedMonth.month + 1}-01`);
		const numDaysToPreviousMonday = (firstDayOfMonth.day() + WEEK_RANGE_SHIFT) % NUM_DAYS_IN_WEEK;
		const mondayBeforeFirstDay = firstDayOfMonth.subtract(numDaysToPreviousMonday, EDurations.DAY);
		const days: WeekDay[] = Array.from({ length: NUM_DAYS_IN_SIX_WEEKS }, (_, i) => {
			const date = mondayBeforeFirstDay.add(i, EDurations.DAY);
			return {
				date: date.format('YYYY-MM-DD'),
				dayOfMonth: date.date(),
				isCurrentMonth: date.month() === selectedMonth.month,
				isDisabled: disabledDates && disabledDates(date),
			};
		});
		return days;
	};

	const goBack = () => {
		setSelectedMonth({
			month: dayjs(dayjs().month(selectedMonth.month).year(selectedMonth.year).subtract(1, 'month')).month(),
			year: dayjs(dayjs().month(selectedMonth.month).year(selectedMonth.year).subtract(1, 'month')).year(),
		});
	};

	const goForward = () => {
		setSelectedMonth({
			month: dayjs(dayjs().month(selectedMonth.month).year(selectedMonth.year).add(1, 'month')).month(),
			year: dayjs(dayjs().month(selectedMonth.month).year(selectedMonth.year).add(1, 'month')).year(),
		});
	};

	useEffect(() => {
		const days = createCalendar();
		const chunkDays = () => {
			const results: WeekDay[][] = [];
			while (days.length) {
				results.push(days.splice(0, 7));
			}
			return results;
		};

		setWeeksAndDays(chunkDays());
	}, [selectedMonth]);

	return (
		<StyledCalendar
			theme={{
				...colors,
			}}
		>
			<div data-aid="Date-calendar-head" className="calendar_header">
				<button data-aid="button-Date-back" onClick={() => goBack()}>
					<BnBack />
				</button>
				<span style={{ paddingBottom: '4px' }}>{`${dayjs().month(selectedMonth.month).format('MMMM')} ${dayjs()
					.year(selectedMonth.year)
					.format('YYYY')}`}</span>
				<button data-aid="button-Date-forward" onClick={() => goForward()}>
					<BnForward />
				</button>
			</div>
			<div data-aid="Date-calendar_body" className="calendar_body">
				<table>
					<thead>
						<tr>
							{weekDays.map((weekDay: string, key: number) => (
								<th key={key}>{weekDay}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{weeksAndDays?.map((week: WeekDay[], i: number) => (
							<tr key={i}>
								{week.map((day: WeekDay, a: number) => (
									<td key={a}>
										<div
											className={cx({
												disabledDay: day.isDisabled,
												notCurrentMonth: !day.isCurrentMonth,
												selected: day.date === selectedDay.format('YYYY-MM-DD'),
												today: day.date === dayjs().format('YYYY-MM-DD'),
											})}
											onMouseEnter={() =>
												!day.isDisabled &&
												setHoverPlaceholder(
													dayjs(day.date)
														.set('hour', selectedDay.get('hour'))
														.set('minute', selectedDay.get('minute'))
														.format(getValueFormat())
												)
											}
											onMouseOut={() => setHoverPlaceholder(undefined)}
											onClick={() => {
												if (!day.isDisabled) {
													setInputValue(
														dayjs(day.date)
															.set('hour', selectedDay.get('hour'))
															.set('minute', selectedDay.get('minute'))
															.format(getValueFormat())
													);
													if (onChange) {
														onChange(
															dayjs(day.date, 'YYYY-MM-DD')
																.set('hour', selectedDay.get('hour'))
																.set('minute', selectedDay.get('minute'))
														);
													}
													setSelectedDay(dayjs(day.date));
													// selectedDay.set(
													//   'date',
													//   dayjs(day.date, 'YYYY-MM-DD').get('date')
													// )
													//   );
													if (!day.isCurrentMonth) {
														setSelectedMonth({
															month: dayjs(day.date).month(),
															year: dayjs(day.date).year(),
														});
													}
													setIsOptionsOpen(false);
												}
											}}
										>
											{dayjs(day.date).format('D')}
										</div>
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</StyledCalendar>
	);
};
